import React from 'react';
import { Icon, Tooltip } from '@/components/generic';
import { cn } from '@/lib/utils';

type FieldProps = {
  className?: string;
  classInput?: string;
  label?: string;
  innerLabel?: string;
  tooltip?: string;
  children?: React.ReactNode;
  textarea?: boolean;
  type?: string;
  validated?: boolean;
  error?: boolean;
  handleForgotPassword?: () => void;
  icon?: string | React.ReactNode;
};

export const Field = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FieldProps &
    React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(
  (
    {
      className,
      classInput,
      label,
      innerLabel,
      tooltip,
      children,
      textarea,
      type,
      validated,
      error,
      handleForgotPassword,
      icon,
      ...inputProps
    },
    ref,
  ) => {
    return (
      <div className={cn(innerLabel && 'pt-1.5', className || '')}>
        {label && !innerLabel && (
          <div className='flex items-center mb-4'>
            <div className='text-button text-t-primary'>{label}</div>
            {tooltip && <Tooltip className='ml-1.5' content={tooltip} />}
          </div>
        )}
        <div className={cn(`relative`, textarea && 'text-0')}>
          {innerLabel && !label && (
            <div className='absolute -top-2.5 left-6 px-1 bg-b-surface2 text-caption pointer-events-none'>
              {innerLabel}
            </div>
          )}
          {handleForgotPassword && (
            <button
              className='absolute -top-2.5 right-6 px-1 bg-b-surface2 text-caption text-t-secondary transition-colors hover:text-t-primary'
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          )}
          {children}
          {textarea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              className={cn(
                `w-full h-24 px-4.5 py-3.5 border border-s-stroke2 rounded-3xl text-body-2 text-t-primary outline-none transition-colors resize-none hover:border-s-highlight focus:border-s-highlight placeholder:text-t-secondary/50`,
                validated && 'pr-14',
                error && 'pr-14',
                innerLabel && 'pl-6.5',
                classInput || '',
              )}
              {...inputProps}
            ></textarea>
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              className={cn(
                `w-full h-12 px-4.5 border border-s-stroke2 rounded-full text-body-2 text-t-primary outline-none transition-colors hover:border-s-highlight focus:border-s-highlight placeholder:text-t-secondary/50`,
                validated && 'pr-14',
                error && 'pr-14',
                icon && 'pr-14',
                (validated || error) && icon && 'pr-20',
                innerLabel && 'pl-6.5',
                classInput || '',
              )}
              type={type || 'text'}
              {...inputProps}
            />
          )}
          {validated && !error && (
            <Icon
              className='absolute top-1/2 right-5 -translate-y-1/2 fill-primary-02'
              name='check'
            />
          )}
          {error && (
            <Icon
              className='absolute top-1/2 right-5 -translate-y-1/2 fill-red-500'
              name='close'
            />
          )}
          {icon && icon}
        </div>
      </div>
    );
  },
);

Field.displayName = 'Field';
